import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { mergeClasses } from '@/utils';

import { ChevronDownIcon, ChevronUpIcon, CloseCircleIcon, CloseIcon } from '@/assets/icons';
import {
  Badge,
  Button,
  Checkbox,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SvgIcon,
} from '@/components/common';

import type {
  IMultiSelect,
  IMultiSelectGroup,
  IMultiSelectOption,
  IMultiSelectRef,
} from './MultiSelect.interfaces';
import { useDebounce } from '@/hooks';
import { renderLabelStyle } from '../DropdownSearch/DropdownSearch.utils';

const MultiSelect = forwardRef<IMultiSelectRef, IMultiSelect>(
  (
    {
      options,
      onValueChange,
      minWidth,
      maxWidth,
      width,
      className,
      defaultValue = [],
      placeholder = 'Select options',
      maxCount = 2,
      modalPopover = false,
      hideSelectAll = false,
      searchable = true,
      emptyIndicator,
      autoSize = false,
      singleLine = false,
      popoverClassName,
      disabled = false,
      deduplicateOptions = false,
      resetOnDefaultValueChange = true,
      closeOnSelect = false,
      searchCallback,
      label,
      required,
      error,
      defaultCacheOptions,
      isLabelstyled = false,
    },
    ref,
  ) => {
    const [selectedValues, setSelectedValues] = useState<string[]>(defaultValue);
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const debouncedSearchValue = useDebounce(searchValue);
    const [optionsState, setOptionsState] = useState(options || []);
    const [searchCursor, setSearchCursor] = useState<string | undefined>(undefined);
    const [isEndReached, setEndReached] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const buttonRef = useRef<HTMLButtonElement>(null);
    const prevDefaultValueRef = useRef<string[]>(defaultValue);
    const selectedOptionsCache = useRef<Map<string, IMultiSelectOption>>(
      new Map(defaultCacheOptions?.map((option) => [option.value, option])),
    );

    const effectiveWidth = useMemo(() => {
      if (width) return width;
      if (autoSize) return 'auto';
      return '100%';
    }, [width, autoSize]);

    const isGroupedOptions = useCallback(
      (opts: IMultiSelectOption[] | IMultiSelectGroup[]): opts is IMultiSelectGroup[] => {
        return opts.length > 0 && 'heading' in opts[0];
      },
      [],
    );

    const arraysEqual = useCallback((a: string[], b: string[]): boolean => {
      if (a.length !== b.length) return false;
      const sortedA = [...a].sort();
      const sortedB = [...b].sort();
      return sortedA.every((val, index) => val === sortedB[index]);
    }, []);

    const resetToDefault = useCallback(() => {
      setSelectedValues(defaultValue);
      setIsPopoverOpen(false);
      setSearchValue('');
      onValueChange(defaultValue);
    }, [defaultValue, onValueChange]);

    useImperativeHandle(
      ref,
      () => ({
        reset: resetToDefault,
        getSelectedValues: () => selectedValues,
        setSelectedValues: (values: string[]) => {
          setSelectedValues(values);
          onValueChange(values);
        },
        clear: () => {
          setSelectedValues([]);
          onValueChange([]);
        },
        focus: () => {
          if (buttonRef.current) {
            buttonRef.current.focus();
            const originalOutline = buttonRef.current.style.outline;
            const originalOutlineOffset = buttonRef.current.style.outlineOffset;
            buttonRef.current.style.outline = '2px solid hsl(var(--ring))';
            buttonRef.current.style.outlineOffset = '2px';
            setTimeout(() => {
              if (buttonRef.current) {
                buttonRef.current.style.outline = originalOutline;
                buttonRef.current.style.outlineOffset = originalOutlineOffset;
              }
            }, 1000);
          }
        },
      }),
      [resetToDefault, selectedValues, onValueChange],
    );

    const getAllOptions = useMemo((): IMultiSelectOption[] => {
      if (options) {
        if (options.length === 0) return [];

        const allOptions = isGroupedOptions(options)
          ? options.flatMap((group) => group.options)
          : options;

        if (!deduplicateOptions) return allOptions;

        const seen = new Set<string>();
        return allOptions.filter((option) => {
          if (seen.has(option.value)) return false;
          seen.add(option.value);
          return true;
        });
      }
      return optionsState as IMultiSelectOption[];
    }, [options, deduplicateOptions, isGroupedOptions, optionsState]);

    const getOptionByValue = useCallback(
      (value: string): IMultiSelectOption | undefined => {
        return (
          getAllOptions.find((option) => option.value === value) ||
          selectedOptionsCache.current.get(value)
        );
      },
      [getAllOptions],
    );

    useEffect(() => {
      const fetchOptions = async () => {
        if (!searchCallback) return;

        try {
          const serverOptions = await searchCallback({ searchBy: debouncedSearchValue });
          setOptionsState(serverOptions.data);
          setSearchCursor(serverOptions.cursor);
          setEndReached(false);
        } catch (error) {
          console.error('Failed to fetch options:', error);
        }
      };

      fetchOptions();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchValue]);

    const filteredOptions = useMemo(() => {
      // If options are provided via props, filter them locally
      if (options) {
        if (!searchable || !searchValue) return options;
        if (options.length === 0) return [];
        if (isGroupedOptions(options)) {
          return options
            .map((group) => ({
              ...group,
              options: group.options.filter(
                (option) =>
                  option.label.toLowerCase().includes(searchValue.toLowerCase()) ||
                  option.value.toLowerCase().includes(searchValue.toLowerCase()),
              ),
            }))
            .filter((group) => group.options.length > 0);
        }
        return options.filter(
          (option) =>
            option.label.toLowerCase().includes(searchValue.toLowerCase()) ||
            option.value.toLowerCase().includes(searchValue.toLowerCase()),
        );
      }
      return optionsState;
    }, [options, searchValue, searchable, isGroupedOptions, optionsState]);

    const loadMoreOptions = async () => {
      if (!searchCallback || isLoadingMore || isEndReached || !searchCursor) return;

      setIsLoadingMore(true);
      try {
        const serverOptions = await searchCallback({
          searchBy: debouncedSearchValue,
          cursor: searchCursor,
        });

        // Check if cursor is the same (end reached)
        if (serverOptions.cursor === searchCursor) {
          setEndReached(true);
        } else {
          setOptionsState((prev) => [...prev, ...serverOptions.data] as typeof prev);
          setSearchCursor(serverOptions.cursor);
        }
      } catch (error) {
        console.error('Failed to load more options:', error);
      } finally {
        setIsLoadingMore(false);
      }
    };

    const onScroll = (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      const { scrollTop, scrollHeight, clientHeight } = target;

      // Check if scrolled to bottom (with small threshold)
      if (scrollHeight - scrollTop <= clientHeight + 10) {
        loadMoreOptions();
      }
    };

    useEffect(() => {
      if (!isPopoverOpen) {
        setSearchValue('');
        setSearchCursor(undefined);
        setEndReached(false);
      }
    }, [isPopoverOpen]);

    const onToggleOption = (optionValue: string) => {
      if (disabled) return;
      const option = getOptionByValue(optionValue);
      if (option?.disabled) return;
      const isDeselecting = selectedValues.includes(optionValue);
      const newSelectedValues = isDeselecting
        ? selectedValues.filter((value) => value !== optionValue)
        : [...selectedValues, optionValue];
      if (isDeselecting) {
        selectedOptionsCache.current.delete(optionValue);
      } else if (option) {
        selectedOptionsCache.current.set(optionValue, option);
      }
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
      if (closeOnSelect) {
        setIsPopoverOpen(false);
      }
    };

    const clearAllSelected = () => {
      if (disabled) return;
      selectedOptionsCache.current.clear();
      setSelectedValues([]);
      onValueChange([]);
    };

    const onTogglePopover = () => {
      if (disabled) return;
      setIsPopoverOpen((prev) => !prev);
    };

    const clearExtraOptions = () => {
      if (disabled) return;
      const newSelectedValues = selectedValues.slice(0, maxCount);
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    };

    const selectableOptions = useMemo(
      () => getAllOptions.filter((option) => !option.disabled),
      [getAllOptions],
    );

    const filteredSelectableOptions = useMemo((): IMultiSelectOption[] => {
      if (!searchable || !searchValue) return selectableOptions;

      const flatFiltered = isGroupedOptions(filteredOptions)
        ? filteredOptions.flatMap((group) => group.options)
        : filteredOptions;

      return flatFiltered.filter((option) => !option.disabled);
    }, [filteredOptions, searchValue, searchable, selectableOptions, isGroupedOptions]);

    const areAllSelected = useMemo(
      () =>
        filteredSelectableOptions.length > 0 &&
        filteredSelectableOptions.every((option) => selectedValues.includes(option.value)),
      [selectedValues, filteredSelectableOptions],
    );

    const onToggleAllOption = () => {
      if (disabled) return;

      if (areAllSelected) {
        setSelectedValues([]);
        onValueChange([]);
      } else {
        const newSelectedValues = filteredSelectableOptions.map((o) => o.value);
        setSelectedValues(newSelectedValues);
        onValueChange(newSelectedValues);
      }

      if (closeOnSelect) {
        setIsPopoverOpen(false);
      }
    };

    useEffect(() => {
      if (!resetOnDefaultValueChange) return;
      const prevDefaultValue = prevDefaultValueRef.current;
      if (!arraysEqual(prevDefaultValue, defaultValue)) {
        if (!arraysEqual(selectedValues, defaultValue)) {
          setSelectedValues(defaultValue);
        }
        prevDefaultValueRef.current = [...defaultValue];
      }
    }, [defaultValue, selectedValues, arraysEqual, resetOnDefaultValueChange]);

    useEffect(() => {
      if (!isPopoverOpen) {
        setSearchValue('');
      }
    }, [isPopoverOpen]);

    return (
      <div className={mergeClasses('flex flex-col gap-2 w-full', className)}>
        {label && (
          <label className="text-sm font-semibold text-neutral-n9">
            {label}
            {required && <span className="ml-1 text-semantic-error-base">*</span>}
          </label>
        )}
        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen} modal={modalPopover}>
          <PopoverTrigger asChild>
            <Button
              ref={buttonRef}
              onClick={onTogglePopover}
              disabled={disabled}
              role="combobox"
              aria-expanded={isPopoverOpen}
              aria-haspopup="listbox"
              aria-label={`${placeholder}: ${selectedValues.length} of ${getAllOptions.length} selected`}
              className={mergeClasses(
                'flex p-1 rounded-lg border border-neutral-n4 !min-h-11 h-auto items-center justify-between bg-white hover:shadow-none hover:border-neutral-n6 active:shadow-none data-[state=open]:border-brand-primary-base [&_svg]:pointer-events-auto px-4 !py-1.5',
                disabled && 'opacity-50 cursor-not-allowed',
                error && '!border-semantic-error-base',
              )}
              style={{
                minWidth: minWidth || '200px',
                maxWidth: maxWidth || '100%',
                width: effectiveWidth,
              }}
            >
              {selectedValues.length > 0 ? (
                <div className="flex justify-between items-center w-full">
                  <div
                    className={mergeClasses(
                      'flex items-center gap-1.5 w-full',
                      singleLine ? 'overflow-x-auto multiselect-singleline-scroll' : 'flex-wrap',
                    )}
                  >
                    {(maxCount === -1 ? selectedValues : selectedValues.slice(0, maxCount))
                      .map((value) => {
                        const option = getOptionByValue(value);
                        if (!option) {
                          return null;
                        }
                        return (
                          <Badge
                            key={value}
                            variant="cancelled"
                            className="border-transparent !h-7 font-semibold text-xs !text-neutral-n8 m-0 gap-[4px] max-w-full"
                          >
                            <span className="truncate">{option.label}</span>
                            <CloseCircleIcon
                              className="h-4 w-4 shrink-0"
                              onClick={(event) => {
                                event.stopPropagation();
                                onToggleOption(value);
                              }}
                            />
                          </Badge>
                        );
                      })
                      .filter(Boolean)}
                    {maxCount !== -1 && selectedValues.length > maxCount && (
                      <Badge
                        variant="cancelled"
                        className="border-transparent !h-7 font-semibold text-xs !text-neutral-n8 m-0"
                      >
                        <span>{`+ ${selectedValues.length - maxCount} item${
                          selectedValues.length - maxCount > 1 ? 's' : ''
                        }`}</span>
                        <CloseCircleIcon
                          className="h-4 w-4"
                          onClick={(event) => {
                            event.stopPropagation();
                            clearExtraOptions();
                          }}
                        />
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between ml-2">
                    <CloseIcon
                      className="w-4 h-4 cursor-pointer text-neutral-n6 hover:text-brand-primary-darkest mr-3"
                      onClick={(event) => {
                        event.stopPropagation();
                        clearAllSelected();
                      }}
                    />
                    <SvgIcon
                      className={mergeClasses(
                        'w-5 h-5 cursor-pointer text-brand-primary-darkest',
                        isPopoverOpen && '!text-brand-primary-base',
                      )}
                      component={isPopoverOpen ? ChevronUpIcon : ChevronDownIcon}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between w-full mx-auto">
                  <span className="text-sm text-neutral-n7 font-medium">{placeholder}</span>
                  <SvgIcon
                    className={mergeClasses(
                      'w-5 h-5 cursor-pointer text-brand-primary-darkest',
                      isPopoverOpen && '!text-brand-primary-base',
                    )}
                    component={isPopoverOpen ? ChevronUpIcon : ChevronDownIcon}
                  />
                </div>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            role="listbox"
            aria-multiselectable="true"
            aria-label="Available options"
            className={mergeClasses(
              'w-auto min-w-[300px] shadow-none border border-solid border-neutral-n4',
              popoverClassName,
            )}
            style={{
              minWidth: minWidth || '200px',
              maxWidth: maxWidth || '100%',
              width: buttonRef.current?.offsetWidth || effectiveWidth,
              maxHeight: '60vh',
            }}
            align="start"
            onEscapeKeyDown={() => setIsPopoverOpen(false)}
          >
            <Command className="rounded-soft" shouldFilter={false}>
              {searchable && (
                <div className="flex flex-row items-center justify-start px-4 py-3 h-10 border-b border-neutral-n4">
                  {!hideSelectAll && (
                    <Checkbox onClick={onToggleAllOption} checked={areAllSelected} />
                  )}
                  <CommandInput
                    placeholder="Search"
                    value={searchValue}
                    onValueChange={setSearchValue}
                    aria-label="Search options"
                    className="w-full"
                  />
                </div>
              )}
              <CommandList
                onScroll={onScroll}
                className="max-h-[40vh] overflow-y-auto multiselect-scrollbar overscroll-behavior-y-contain [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
              >
                <CommandEmpty>{emptyIndicator || 'No results found.'}</CommandEmpty>{' '}
                {isGroupedOptions(filteredOptions) ? (
                  filteredOptions.map((group) => (
                    <CommandGroup key={group.heading} heading={group.heading}>
                      {group.options.map((option) => {
                        const isSelected = selectedValues.includes(option.value);
                        return (
                          <CommandItem
                            key={option.value}
                            onSelect={() => onToggleOption(option.value)}
                            role="option"
                            aria-selected={isSelected}
                            aria-disabled={option.disabled}
                            aria-label={`${option.label}${
                              isSelected ? ', selected' : ', not selected'
                            }${option.disabled ? ', disabled' : ''}`}
                            className={mergeClasses(
                              'cursor-pointer min-h-10 gap-3',
                              option.disabled && 'opacity-50 cursor-not-allowed',
                            )}
                            disabled={option.disabled}
                          >
                            <Checkbox checked={isSelected} disabled={option.disabled} />
                            <span>
                              {isLabelstyled ? renderLabelStyle(option.label) : option.label}
                            </span>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  ))
                ) : (
                  <CommandGroup className="!p-0">
                    {filteredOptions.map((option) => {
                      const isSelected = selectedValues.includes(option.value);
                      return (
                        <CommandItem
                          key={option.value}
                          onSelect={() => onToggleOption(option.value)}
                          role="option"
                          aria-selected={isSelected}
                          aria-disabled={option.disabled}
                          aria-label={`${option.label}${
                            isSelected ? ', selected' : ', not selected'
                          }${option.disabled ? ', disabled' : ''}`}
                          className={mergeClasses(
                            'cursor-pointer min-h-10 gap-3',
                            option.disabled && 'opacity-50 cursor-not-allowed',
                          )}
                          disabled={option.disabled}
                        >
                          <Checkbox checked={isSelected} disabled={option.disabled} />
                          <span>
                            {isLabelstyled ? renderLabelStyle(option.label) : option.label}
                          </span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {error && <p className="text-xs text-semantic-error-base">{error}</p>}
      </div>
    );
  },
);

MultiSelect.displayName = 'MultiSelect';

export default MultiSelect;
